import * as THREE from 'three';
import { box, cyl, M, mat, mesh } from '../materials';
import { signTexture } from '../textures';

export interface BuiltRascal {
  group: THREE.Group;
  /** Spin these by distance traveled (each turns about its own local y). */
  wheels: THREE.Mesh[];
  /** The steering column: turns a little with the front wheel. */
  tiller: THREE.Group;
  /** The orange safety flag on its whip, for flapping. */
  flag: THREE.Group;
  /** The headlight lens, brighter at night. */
  light: THREE.MeshStandardMaterial;
}

/** Top of the seat cushion, in meters. */
export const RASCAL_SEAT = 0.66;
/** The floorboard the rider's feet rest on. */
export const RASCAL_FLOOR = 0.252;

function wheel(r: number, w: number, x: number, z: number): { pivot: THREE.Group; tire: THREE.Mesh } {
  const pivot = new THREE.Group();
  pivot.position.set(x, r, z);
  pivot.rotation.z = Math.PI / 2;
  const tire = cyl(r, r, w, M.rubber, 0, 0, 0, 14);
  // A gray hub on each side so you can see it turn.
  for (const s of [-1, 1])
    tire.add(box(r * 1.1, 0.012, r * 0.35, M.steel, 0, s * (w / 2 + 0.004), 0, { cast: false }));
  pivot.add(tire);
  return { pivot, tire };
}

/**
 * The Lil' Rascal: a three-wheel mobility scooter with a captain's chair, a wire basket
 * (with a twelve-pack in it), a cup holder and a tall orange flag so the trucks can see
 * you. Built facing +z with the origin on the ground under the seat, where the rider's
 * hips go.
 */
export function buildRascal(color = '#b3141f'): BuiltRascal {
  const g = new THREE.Group();
  const paint = mat(color, { rough: 0.35, metal: 0.35 });
  const plastic = mat('#2e3136', { rough: 0.7 });
  const vinyl = mat('#1d1e21', { rough: 0.55 });
  const steel = M.steel;
  const wheels: THREE.Mesh[] = [];

  // Rear wheels under the battery box, a little front wheel under the nose.
  for (const x of [-0.29, 0.29]) {
    const w = wheel(0.13, 0.07, x, -0.3);
    g.add(w.pivot);
    wheels.push(w.tire);
  }
  const front = wheel(0.11, 0.06, 0, 0.78);
  g.add(front.pivot);
  wheels.push(front.tire);

  // Battery box, floorboard and the nose fairing.
  g.add(box(0.56, 0.24, 0.36, paint, 0, 0.3, -0.3));
  g.add(box(0.5, 0.08, 0.95, plastic, 0, 0.2, 0.225));
  g.add(box(0.44, 0.012, 0.66, mat('#121212', { rough: 1 }), 0, 0.246, 0.28, { cast: false }));
  g.add(box(0.36, 0.3, 0.3, paint, 0, 0.32, 0.74));
  g.add(box(0.3, 0.06, 0.32, plastic, 0, 0.46, 0.74));
  const light = new THREE.MeshStandardMaterial({
    color: '#fffbe6',
    emissive: new THREE.Color('#fff2b0'),
    emissiveIntensity: 0.4,
    roughness: 0.2,
  });
  g.add(cyl(0.05, 0.05, 0.03, light, 0, 0.38, 0.905, 12, { cast: false }).rotateX(Math.PI / 2));
  g.add(cyl(0.065, 0.065, 0.025, M.chrome, 0, 0.38, 0.895, 12, { cast: false }).rotateX(Math.PI / 2));

  // The tiller leans back toward the rider, with a loop handlebar and a dash.
  const tiller = new THREE.Group();
  tiller.position.set(0, 0.47, 0.72);
  tiller.rotation.x = -0.32;
  tiller.add(cyl(0.032, 0.036, 0.46, plastic, 0, 0.22, 0, 10));
  tiller.add(box(0.2, 0.07, 0.12, plastic, 0, 0.47, 0.03));
  // Battery gauge: three lights.
  ['#39d353', '#f2c200', '#e5484d'].forEach((c, i) =>
    tiller.add(
      box(0.03, 0.012, 0.03, mat(c, { emissive: c, emissiveIntensity: 0.8 }), -0.05 + i * 0.05, 0.51, 0.02, {
        cast: false,
      }),
    ),
  );
  tiller.add(cyl(0.017, 0.017, 0.56, steel, 0, 0.47, -0.04, 8).rotateZ(Math.PI / 2));
  for (const s of [-1, 1])
    tiller.add(cyl(0.024, 0.024, 0.11, vinyl, s * 0.25, 0.47, -0.04, 8).rotateZ(Math.PI / 2));
  // The basket, with a twelve-pack riding up front.
  const basket = new THREE.Group();
  basket.position.set(0, 0.22, 0.14);
  const wire = mat('#9aa0a6', { rough: 0.4, metal: 0.6 });
  basket.add(box(0.36, 0.012, 0.24, wire, 0, -0.1, 0));
  for (const x of [-0.18, 0.18]) basket.add(box(0.012, 0.22, 0.24, wire, x, 0, 0));
  for (const z of [-0.12, 0.12]) basket.add(box(0.36, 0.22, 0.012, wire, 0, 0, z));
  for (const x of [-0.09, 0, 0.09]) basket.add(box(0.006, 0.2, 0.25, wire, x, 0, 0, { cast: false }));
  basket.add(box(0.26, 0.15, 0.17, mat('#2f6fd6'), 0, -0.02, 0));
  basket.add(box(0.262, 0.03, 0.172, mat('#f4f4f0'), 0, 0.02, 0, { cast: false }));
  tiller.add(basket);
  g.add(tiller);

  // The captain's chair: post, cushion, backrest and flip-up armrests.
  g.add(cyl(0.04, 0.05, 0.3, steel, 0, 0.39, -0.08, 10));
  g.add(box(0.44, 0.1, 0.4, vinyl, 0, RASCAL_SEAT - 0.05, -0.08));
  const back = box(0.42, 0.42, 0.08, vinyl, 0, 0.88, -0.3);
  back.rotation.x = -0.12;
  g.add(back);
  for (const s of [-1, 1]) {
    g.add(box(0.05, 0.04, 0.32, vinyl, s * 0.245, 0.79, -0.08));
    g.add(box(0.03, 0.15, 0.03, steel, s * 0.245, 0.7, 0.06));
  }
  // A cup holder on the right armrest, occupied.
  g.add(cyl(0.045, 0.04, 0.06, plastic, -0.27, 0.79, 0.1, 10));
  g.add(cyl(0.034, 0.034, 0.12, mat('#2f6fd6', { rough: 0.35, metal: 0.5 }), -0.27, 0.84, 0.1, 10));

  // Vanity plate and a bumper sticker on the back of the battery box.
  const plate = mesh(
    new THREE.PlaneGeometry(0.2, 0.1),
    new THREE.MeshStandardMaterial({
      map: signTexture({
        w: 256,
        h: 128,
        bg: '#f4f1e6',
        fg: '#1d3d8f',
        border: '#1d3d8f',
        borderWidth: 6,
        lines: [
          { text: 'RASCAL', size: 64, font: 'Anton' },
          { text: 'LUCKY ACRES', size: 22, font: 'Anton', color: '#c62828' },
        ],
      }),
      roughness: 0.5,
    }),
    -0.13,
    0.31,
    -0.4815,
    { cast: false },
  );
  plate.rotation.y = Math.PI;
  g.add(plate);
  const sticker = mesh(
    new THREE.PlaneGeometry(0.24, 0.08),
    new THREE.MeshStandardMaterial({
      map: signTexture({
        w: 384,
        h: 128,
        bg: '#ffd400',
        fg: '#111',
        lines: [
          { text: 'MY OTHER RIDE', size: 40, font: 'Anton' },
          { text: 'IS A CAMARO', size: 40, font: 'Anton' },
        ],
        gap: 4,
      }),
      roughness: 0.6,
    }),
    0.13,
    0.31,
    -0.4815,
    { cast: false },
  );
  sticker.rotation.y = Math.PI;
  g.add(sticker);

  // The flag: so the eighteen-wheelers see you coming. They will not stop.
  g.add(cyl(0.007, 0.007, 1.5, mat('#e8e8e8', { rough: 0.4 }), 0.22, 1.17, -0.46, 6));
  const flag = new THREE.Group();
  flag.position.set(0.22, 1.9, -0.46);
  const pennant = new THREE.Shape();
  pennant.moveTo(0, 0);
  pennant.lineTo(-0.34, -0.09);
  pennant.lineTo(0, -0.2);
  pennant.closePath();
  flag.add(
    mesh(
      new THREE.ShapeGeometry(pennant),
      mat('#ff6a00', { rough: 0.8, side: THREE.DoubleSide, emissive: '#ff6a00', emissiveIntensity: 0.15 }),
      0,
      0,
      0,
      { cast: false },
    ),
  );
  g.add(flag);

  g.traverse((o) => (o.userData.noBatch = true));
  return { group: g, wheels, tiller, flag, light };
}
